import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { IMetricConfig, ISenderPerformanceRow, IWorkspacePerformance, METRICS, PerformanceMetric } from './performanceMetrics';

export function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export const toCsv = (rows: (string | number)[][]) => rows.map(r => r.map(csvCell).join(',')).join('\n');

const fileStem = (data: { from: string; to: string }) => `campaign-performance_${data.from}_${data.to}`;

export function exportWorkspaceCsv(data: IWorkspacePerformance, metrics: IMetricConfig[] = METRICS) {
  const header = [data.granularity === 'month' ? 'Month' : 'Date', ...metrics.map(m => m.label)];
  const rows = data.series.map(b => [b.key, ...metrics.map(m => b[m.key])]);
  const totals = ['Total', ...metrics.map(m => data.totals[m.key])];
  // BOM so Excel opens it as UTF-8.
  download(new Blob(['﻿', toCsv([header, ...rows, totals])], { type: 'text/csv;charset=utf-8' }), `${fileStem(data)}.csv`);
}

export function exportSendersCsv(rows: ISenderPerformanceRow[], range: { from: string; to: string }) {
  const header = ['Sender', 'Live campaigns', ...METRICS.map(m => m.label)];
  const body = rows.map(r => [`${r.name || 'Unnamed sender'}${r.isMe ? ' (Me)' : ''}`, r.campaignCount, ...METRICS.map(m => r[m.key])]);
  download(new Blob(['﻿', toCsv([header, ...body])], { type: 'text/csv;charset=utf-8' }), `${fileStem(range)}_senders.csv`);
}

const escapeXml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The card's own icon as SVG markup, coloured in — `currentColor` means nothing in an exported file. */
function metricIconSvg(m: IMetricConfig, x: number, y: number, size: number): string {
  const markup = renderToStaticMarkup(createElement(m.Icon, { size, strokeWidth: 1.5 }))
    .replace(/currentColor/g, m.color);
  return `<g transform="translate(${x} ${y})">${markup}</g>`;
}

interface ISnapshotInput {
  data: IWorkspacePerformance;
  metrics?: IMetricConfig[];
  title?: string;
  hidden: Set<PerformanceMetric>;
  subtitle: string;
  chartImage: string | null;
  chartSize: { width: number; height: number };
}

/**
 * The stat cards and the chart as one SVG. Cards are drawn as vectors; the chart is
 * embedded as the PNG chart.js already rendered, so it matches the screen exactly.
 */
function buildSnapshotSvg({ data, metrics = METRICS, title = 'Performance', hidden, subtitle, chartImage, chartSize }: ISnapshotInput): { svg: string; width: number; height: number } {
  const pad = 24;
  const gap = 16;
  const width = Math.max(960, Math.round(chartSize.width) + pad * 2);
  const cardH = 94;
  const headerH = 72;
  const cardsTop = headerH;
  const rowCount = Math.ceil(metrics.length / 4);
  const chartTop = cardsTop + cardH * rowCount + gap * (rowCount - 1) + 24;
  const chartH = chartImage ? Math.round(chartSize.height * ((width - pad * 2) / Math.max(1, chartSize.width))) : 0;
  const height = chartTop + chartH + pad;
  const font = `font-family="Helvetica, Arial, sans-serif"`;

  const cards = metrics.map((m: IMetricConfig, i) => {
    // Same layout as the page: rows of four, a shorter last row stretched across.
    const row = Math.floor(i / 4);
    const inRow = Math.min(4, metrics.length - row * 4);
    const w = (width - pad * 2 - gap * (inRow - 1)) / inRow;
    const x = pad + (i % 4) * (w + gap);
    const y = cardsTop + row * (cardH + gap);
    const opacity = hidden.has(m.key) ? 0.45 : 1;
    return `
      <g opacity="${opacity}">
        <rect x="${x}" y="${y}" width="${w}" height="${cardH}" rx="12" fill="#FFFFFF" stroke="#E5E7EB"/>
        <text x="${x + 16}" y="${y + 30}" ${font} font-size="14" font-weight="600" fill="#252B37">${escapeXml(m.label)}</text>
        <rect x="${x + 16.5}" y="${y + 48.5}" width="29" height="29" rx="8" fill="${m.iconBg}" stroke="${m.iconBorder}"/>
        ${metricIconSvg(m, x + 21, y + 53, 20)}
        <text x="${x + 56}" y="${y + 72}" ${font} font-size="24" font-weight="500" fill="#252B37">${data.totals[m.key]}</text>
      </g>`;
  }).join('');

  const chart = chartImage
    ? `<image x="${pad}" y="${chartTop}" width="${width - pad * 2}" height="${chartH}" href="${chartImage}" xlink:href="${chartImage}"/>`
    : '';

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="#FFFFFF"/>
  <text x="${pad}" y="38" ${font} font-size="20" font-weight="700" fill="#111927">${escapeXml(title)}</text>
  <text x="${pad}" y="58" ${font} font-size="13" fill="#6B7280">${escapeXml(subtitle)}</text>
  ${cards}
  ${chart}
</svg>`;

  return { svg, width, height };
}

export function exportSnapshotSvg(input: ISnapshotInput) {
  const { svg } = buildSnapshotSvg(input);
  download(new Blob([svg], { type: 'image/svg+xml' }), `${fileStem(input.data)}.svg`);
}

export function exportSnapshotPng(input: ISnapshotInput): Promise<void> {
  const { svg, width, height } = buildSnapshotSvg(input);
  const scale = 2;
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas unavailable')); return; }
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('PNG export failed')); return; }
        download(blob, `${fileStem(input.data)}.png`);
        resolve();
      }, 'image/png');
    };
    img.onerror = () => reject(new Error('PNG export failed'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}
